/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  EntityType,
  NewAgencyLocationRequestOriginType,
} from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface NewAgencyLocationRequestProps {
  id: string;
  phoneNumber: string;
  name: string;
  requestedByPersonId: string;
  entityId: string | null;
  entityType: EntityType;
  type: NewAgencyLocationRequestOriginType;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class NewAgencyLocationRequestRecord extends Record<NewAgencyLocationRequestProps>({
  id: null as any,
  phoneNumber: null as any,
  name: null as any,
  requestedByPersonId: null as any,
  entityId: null as any,
  entityType: null as any,
  type: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    phoneNumber: string;
    name: string;
    requestedByPersonId: string;
    entityId: string | null;
    entityType: EntityType;
    type: NewAgencyLocationRequestOriginType;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): NewAgencyLocationRequestRecord {
    if (json == null) {
      throw new Error('NewAgencyLocationRequestRecord.fromJS: parameter cannot be null');
    }
    return new NewAgencyLocationRequestRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'NewAgencyLocationRequestJson.id',
      ),
      phoneNumber: getFromOrThrow(
        json,
        (j) => j.phoneNumber,
        'NewAgencyLocationRequestJson.phoneNumber',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'NewAgencyLocationRequestJson.name'),
      requestedByPersonId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'requestedByPersonId', '00000000-0000-0000-0000-000000000000'),
        'NewAgencyLocationRequestJson.requestedByPersonId',
      ),
      entityId: json.entityId,
      entityType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'entityType', EntityType.Unspecified),
        'NewAgencyLocationRequestJson.entityType',
      ),
      type: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'type', NewAgencyLocationRequestOriginType.Requester),
        'NewAgencyLocationRequestJson.type',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    phoneNumber: string;
    name: string;
    requestedByPersonId: string;
    entityId: string | null;
    entityType: EntityType;
    type: NewAgencyLocationRequestOriginType;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [NewAgencyLocationRequestRecord, Error[]] {
    if (json == null) {
      throw new Error('NewAgencyLocationRequestRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new NewAgencyLocationRequestRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'NewAgencyLocationRequestJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      phoneNumber: tryOrDefault(
        () =>
          getFromOrThrow(json, (j) => j.phoneNumber, 'NewAgencyLocationRequestJson.phoneNumber'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'NewAgencyLocationRequestJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestedByPersonId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'requestedByPersonId', '00000000-0000-0000-0000-000000000000'),
            'NewAgencyLocationRequestJson.requestedByPersonId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityId: tryOrDefault(
        () => json.entityId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'entityType', EntityType.Unspecified),
            'NewAgencyLocationRequestJson.entityType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'type', NewAgencyLocationRequestOriginType.Requester),
            'NewAgencyLocationRequestJson.type',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: NewAgencyLocationRequestProps) {
    super(props);
    if (props == null) {
      throw new Error('NewAgencyLocationRequestRecord constructor parameter cannot be null');
    }
  }
}

export abstract class NewAgencyLocationRequestViewModelBase
  implements NewAgencyLocationRequestProps
{
  protected readonly _props: NewAgencyLocationRequestProps;
  private _json?: NewAgencyLocationRequestProps;

  get id(): string {
    return this._props.id;
  }
  get phoneNumber(): string {
    return this._props.phoneNumber;
  }
  get name(): string {
    return this._props.name;
  }
  get requestedByPersonId(): string {
    return this._props.requestedByPersonId;
  }
  get entityId(): string | null {
    return this._props.entityId;
  }
  get entityType(): EntityType {
    return this._props.entityType;
  }
  get type(): NewAgencyLocationRequestOriginType {
    return this._props.type;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAirport() {
    return this._props.entityType === EntityType.Airport;
  }
  public get isBase() {
    return this._props.entityType === EntityType.Base;
  }
  public get isCard() {
    return this._props.entityType === EntityType.Card;
  }
  public get isCommunicationCenter() {
    return this._props.entityType === EntityType.CommunicationCenter;
  }
  public get isCompany() {
    return this._props.entityType === EntityType.Company;
  }
  public get isControlZone() {
    return this._props.entityType === EntityType.ControlZone;
  }
  public get isLocation() {
    return this._props.entityType === EntityType.Location;
  }
  public get isMedicalFacility() {
    return this._props.entityType === EntityType.MedicalFacility;
  }
  public get isOutOfServiceEvent() {
    return this._props.entityType === EntityType.OutOfServiceEvent;
  }
  public get isPaipEvent() {
    return this._props.entityType === EntityType.PaipEvent;
  }
  public get isPerson() {
    return this._props.entityType === EntityType.Person;
  }
  public get isProgram() {
    return this._props.entityType === EntityType.Program;
  }
  public get isRegion() {
    return this._props.entityType === EntityType.Region;
  }
  public get isTimer() {
    return this._props.entityType === EntityType.Timer;
  }
  public get isUnit() {
    return this._props.entityType === EntityType.Unit;
  }
  public get isUnspecified() {
    return this._props.entityType === EntityType.Unspecified;
  }
  public get isVehicle() {
    return this._props.entityType === EntityType.Vehicle;
  }
  public get isArrange() {
    return this._props.type === NewAgencyLocationRequestOriginType.Arrange;
  }
  public get isDeclineOrAbort() {
    return this._props.type === NewAgencyLocationRequestOriginType.DeclineOrAbort;
  }
  public get isOrg() {
    return this._props.type === NewAgencyLocationRequestOriginType.Org;
  }
  public get isPrecautionaryLanding() {
    return this._props.type === NewAgencyLocationRequestOriginType.PrecautionaryLanding;
  }
  public get isRequester() {
    return this._props.type === NewAgencyLocationRequestOriginType.Requester;
  }

  public isAnyEntityType(...entityType: EntityType[]) {
    return entityType.includes(this._props.entityType);
  }
  public isAnyType(...newAgencyLocationRequestOriginType: NewAgencyLocationRequestOriginType[]) {
    return newAgencyLocationRequestOriginType.includes(this._props.type);
  }

  constructor(props: NewAgencyLocationRequestProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        phoneNumber: this._props.phoneNumber,
        name: this._props.name,
        requestedByPersonId: this._props.requestedByPersonId,
        entityId: this._props.entityId,
        entityType: this._props.entityType,
        type: this._props.type,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllNewAgencyLocationRequestEntities = (state: AppState) =>
  state.entityStores.newAgencyLocationRequest.entities;

export const selectNewAgencyLocationRequestById = (id: string) => (state: AppState) =>
  state.entityStores.newAgencyLocationRequest.entities.get(id);

export const selectNewAgencyLocationRequestMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.newAgencyLocationRequest.meta.get(id);

export const selectNewAgencyLocationRequestAndMetadataById = (id: string) =>
  createSelector(
    selectNewAgencyLocationRequestById(id),
    selectNewAgencyLocationRequestMetadataById(id),
    (newAgencyLocationRequest, meta) => ({ newAgencyLocationRequest, meta }),
  );

export function selectNewAgencyLocationRequestEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllNewAgencyLocationRequestEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, NewAgencyLocationRequestRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectNewAgencyLocationRequestEntitiesByPhoneNumber(phoneNumber: string) {
  return createSelector(selectAllNewAgencyLocationRequestEntities, (entities) =>
    entities.filter((e) => e.phoneNumber === phoneNumber).valueSeq(),
  );
}

export function selectNewAgencyLocationRequestEntitiesByName(name: string) {
  return createSelector(selectAllNewAgencyLocationRequestEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectNewAgencyLocationRequestEntitiesByRequestedByPersonId(
  requestedByPersonId: string,
) {
  return createSelector(selectAllNewAgencyLocationRequestEntities, (entities) =>
    entities.filter((e) => e.requestedByPersonId === requestedByPersonId).valueSeq(),
  );
}

export function selectNewAgencyLocationRequestEntitiesByEntityId(entityId: string) {
  return createSelector(selectAllNewAgencyLocationRequestEntities, (entities) =>
    entities.filter((e) => e.entityId === entityId).valueSeq(),
  );
}

export function selectNewAgencyLocationRequestEntitiesByEntityType(entityType: EntityType) {
  return createSelector(selectAllNewAgencyLocationRequestEntities, (entities) =>
    entities.filter((e) => e.entityType === entityType).valueSeq(),
  );
}

export function selectNewAgencyLocationRequestEntitiesByType(
  type: NewAgencyLocationRequestOriginType,
) {
  return createSelector(selectAllNewAgencyLocationRequestEntities, (entities) =>
    entities.filter((e) => e.type === type).valueSeq(),
  );
}
//#endregion Selectors
